import { useState } from 'react'
import { Spinner } from 'react-bootstrap'

import { RoomSession } from '../../../../../models/Room'

import UserWithAvatar from '../../common/user/UserWithAvatar'

import { type ExplainedResult, type ResultExplainerListData } from './models'
import { formatPropertyLabel } from '../FormFills/utils'

const MAX_VISIBLE_ITEMS = 3

interface ResultToExplainListProps {
  listData: ResultExplainerListData
}

const ResultToExplainList: React.FC<ResultToExplainListProps> = ({ listData }) => {
  const [expanded, setExpanded] = useState(false)
  const { title, items } = listData
  const visibleItems = expanded ? items : items.slice(0, MAX_VISIBLE_ITEMS)
  const hiddenCount = items.length - MAX_VISIBLE_ITEMS

  return (
    <div className='d-flex flex-column gap-2'>
      <div className='bold paragraph-medium'>{title}</div>
      {visibleItems.map((item, index) => {
        const nonNullEntries = Object.entries(item).filter(([, v]) => v !== null && v !== undefined && v !== '')
        return (
          <div key={index} className='d-flex flex-column gap-1 p-3 result-to-explain-container'>
            <div className='paragraph-small bold zocks-text-gray-500'>Item {index + 1}</div>
            {nonNullEntries.length > 0 ? (
              nonNullEntries.map(([key, value]) => (
                <div key={key} className='d-flex gap-2 align-items-baseline'>
                  <span className='paragraph-small bold'>{formatPropertyLabel(key)}:</span>
                  <span className='paragraph-medium'>{String(value)}</span>
                </div>
              ))
            ) : (
              <span className='paragraph-small zocks-text-gray-500'>No data available</span>
            )}
          </div>
        )
      })}
      {hiddenCount > 0 ? (
        <button type='button' className='btn btn-link button-small align-self-start p-0' onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      ) : null}
    </div>
  )
}

interface ResultExplainerViewProps {
  resultToExplain: string
  resultListData?: ResultExplainerListData | null
  explanationResult: ExplainedResult | null
  isLoading: boolean
  roomSession?: RoomSession | null
}

export const ResultExplainerView: React.FC<ResultExplainerViewProps> = ({ resultToExplain, resultListData, explanationResult, isLoading, roomSession }) => {
  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center mt-5'>
        <Spinner />
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-2 zocks-py-1.25 zocks-px-1.25'>
      <div className='lead-extra-small'>Result to explain</div>
      {resultListData ? (
        <ResultToExplainList listData={resultListData} />
      ) : (
        <div className='d-flex flex-column p-3 result-to-explain-container'>{resultToExplain}</div>
      )}
      <div className='lead-extra-small'>Explanation</div>
      <div className='d-flex gap-2 p-3 explanation-container'>
        <i className='bi bi-stars zocks-text-primary' />
        <div className='paragraph-medium'>{explanationResult ? explanationResult.explanation : 'Explanation cannot be determined at this time'}</div>
      </div>
      {explanationResult && roomSession ? (
        <div className='mt-4 d-flex flex-column gap-4'>
          {explanationResult.transcript.map((t, idx) => {
            const user = roomSession.users.find((u) => u.id === t.userId)
            const userName = user ? user.name : 'Unknown'

            return (
              <div key={`${t.userId}-${idx}`} className='d-flex flex-column gap-2'>
                <div className='d-flex align-items-center gap-2 result-explainer-user'>
                  <UserWithAvatar name={userName} displayName={false} />
                  <div className='paragraph-medium bold'>{userName}</div>
                </div>
                <div className='d-flex p-3 gap-2 result-explainer-transcript-container'>
                  <i className='bi bi-quote' />
                  <div className='paragraph-medium'>{t.content}</div>
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
